/**
 * gameStatsPdfParser — parses the GameChanger single-game "Game Stats Sheet" PDF
 * (the lighter box-score export, distinct from the scorebook and the season CSV).
 *
 * Layout (verified against a real KC export): two columns — away team x<300, home
 * team x>=300 — each with a BATTING table, a PITCHING table, and inline notes.
 * The notes carry per-pitcher P-S and BF, which is the gold the reconcile layer
 * uses to attribute at-bats to the exact pitcher (even mid-inning).
 *
 * P-S is TOTAL−STRIKES: "44-25" → 44 pitches, 25 strikes, 21 balls.
 */

import { PdfPage, PdfTextItem, groupIntoRows } from './types';
import { ParsedGameStatsSheet, StatsSheetTeam, StatsSheetBatter, StatsSheetPitcher, PageSide } from '../../types/import.types';

const COL_SPLIT = 300;
const isPureInt = (s: string) => /^\d+$/.test(s.trim());
const isDecimalIP = (s: string) => /^\d+\.\d$/.test(s.trim());
const jersey = (items: PdfTextItem[]) => {
  for (const i of items) { const m = i.str.match(/#(\d+)/); if (m) return m[1]; }
  return '';
};
const nameOf = (items: PdfTextItem[], beforeX: number) =>
  items.filter(i => i.x < beforeX && /[A-Za-z]/.test(i.str))
    // Strip the jersey token (#N or (pos)) before joining — handles both split and combined items
    .map(i => i.str.replace(/#\d+\s*(\([A-Za-z]+\))?/g, '').replace(/\([A-Za-z]+\)/g, '').replace(/[…]/g, '').trim())
    .filter(s => s.length > 0)
    .join(' ').replace(/\s+/g, ' ').trim();
const lastName = (n: string) => (n.trim().split(/\s+/).pop() || '').toLowerCase();

export function parseGameStatsSheet(pages: PdfPage[]): ParsedGameStatsSheet {
  const warnings: string[] = [];
  const page = pages[0];
  if (!page) return { source: 'gameStatsSheet', teams: [], parseWarnings: ['Empty PDF.'] };
  const rows = groupIntoRows(page.items, 3);

  // ── Header: "<away> <a> - <h> <home>" + date ────────────────────────────────
  let awayTeam = '', homeTeam = '', date: string | undefined;
  let finalScore: { away: number; home: number } | undefined;
  for (const row of rows.slice(0, 6)) {
    const scoreItem = row.find(i => /^\d+\s*-\s*\d+$/.test(i.str.trim()));
    if (scoreItem && !finalScore) {
      const [a, h] = scoreItem.str.split('-').map(s => parseInt(s.trim(), 10));
      finalScore = { away: a, home: h };
      awayTeam = row.filter(i => i.x < scoreItem.x && /[A-Za-z]/.test(i.str)).map(i => i.str).join(' ').trim();
      homeTeam = row.filter(i => i.x > scoreItem.x && /[A-Za-z]/.test(i.str)).map(i => i.str).join(' ').trim();
    }
    const dm = row.map(i => i.str).join(' ').match(/([A-Za-z]+ \d{1,2}, \d{4})/);
    if (dm && !date) { const d = new Date(dm[1]); if (!isNaN(d.getTime())) date = d.toISOString().split('T')[0]; }
  }

  const battingIdx = rows.findIndex(r => r.some(i => i.str.trim() === 'BATTING'));
  const pitchingIdx = rows.findIndex(r => r.some(i => i.str.trim() === 'PITCHING'));
  if (battingIdx === -1 || pitchingIdx === -1) warnings.push('Could not find BATTING/PITCHING sections.');

  const sideItems = (side: PageSide) => (row: PdfTextItem[]) =>
    row.filter(i => side === 'away' ? i.x < COL_SPLIT : i.x >= COL_SPLIT).sort((a, b) => a.x - b.x);

  function parseSide(side: PageSide, teamName: string): StatsSheetTeam {
    const pick = sideItems(side);
    const nameMaxX = side === 'away' ? 130 : 430; // stats columns begin after the name/pos

    // Batting rows: have a #jersey and 6 pure ints.
    const batters: StatsSheetBatter[] = [];
    for (let r = battingIdx + 1; r < pitchingIdx && r !== -1; r++) {
      const items = pick(rows[r]);
      const num = jersey(items);
      const ints = items.filter(i => isPureInt(i.str)).map(i => parseInt(i.str, 10));
      if (!num || ints.length < 6) continue;
      const [ab, runs, h, rbi, bb, so] = ints;
      batters.push({ name: nameOf(items, nameMaxX), number: num, ab, r: runs, h, rbi, bb, so });
    }

    // Pitching rows: have a #jersey and a decimal IP + 6 ints.
    const pitchers: StatsSheetPitcher[] = [];
    for (let r = pitchingIdx + 1; r < rows.length; r++) {
      const items = pick(rows[r]);
      const num = jersey(items);
      const ip = items.find(i => isDecimalIP(i.str));
      const ints = items.filter(i => isPureInt(i.str)).map(i => parseInt(i.str, 10));
      if (!num || !ip || ints.length < 6) continue;
      const [h, runs, er, bb, so, hr] = ints;
      pitchers.push({ name: nameOf(items, nameMaxX), number: num, ip: parseFloat(ip.str), h, r: runs, er, bb, so, hr });
    }

    // Notes (P-S, BF) — concatenate this side's text below the pitching header.
    const sideText = page.items
      .filter(i => (side === 'away' ? i.x < COL_SPLIT : i.x >= COL_SPLIT) && i.y > (rows[pitchingIdx]?.[0]?.y ?? 0))
      .sort((a, b) => a.y - b.y || a.x - b.x)
      .map(i => i.str).join(' ');

    applyNote(sideText, 'P-S:', pitchers, (p, nums) => { p.pitches = nums[0]; p.strikes = nums[1]; });
    applyNote(sideText, 'BF:', pitchers, (p, nums) => { p.bf = nums[0]; });

    return { teamName, side, batters, pitchers };
  }

  const teams = [parseSide('away', awayTeam), parseSide('home', homeTeam)];
  return { source: 'gameStatsSheet', date, awayTeam, homeTeam, finalScore, teams, parseWarnings: warnings };
}

/** Pull "Name N[-M]" pairs out of a labeled note segment and merge into pitchers by last name. */
function applyNote(sideText: string, label: string, pitchers: StatsSheetPitcher[], set: (p: StatsSheetPitcher, nums: number[]) => void) {
  const start = sideText.indexOf(label);
  if (start === -1) return;
  // segment = from this label to the next note label
  const rest = sideText.slice(start + label.length);
  const nextLabel = rest.search(/\b(P-S:|BF:|WP:|HBP:|E:|TB:|SB:|2B:|3B:|LOB:)/);
  const seg = nextLabel === -1 ? rest : rest.slice(0, nextLabel);
  const re = /([A-Z][A-Za-z]*(?:\s+[A-Z][A-Za-z…]+)?)\s+(\d+)(?:-(\d+))?/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(seg)) !== null) {
    const nums = [parseInt(m[2], 10), ...(m[3] ? [parseInt(m[3], 10)] : [])];
    const ln = lastName(m[1]);
    // Prefix match handles GameChanger truncating long names in the PITCHING table
    // ("G Jaco…" vs the note's untruncated "G Jacobsen"). The truncated side ends
    // in a literal "…" glyph, which is not itself a character of the real name, so
    // it must be stripped before either string can be a prefix of the other —
    // comparing "jaco…".startsWith("jacobsen") (or vice versa) is false forever
    // and silently drops the match, which used to fail the whole team's BF != null
    // guard in applyStatsSheet and disable exact attribution for every pitcher.
    const stem = (s: string) => s.replace(/…+$/, '');
    const p = pitchers.find(pp => {
      const a = stem(lastName(pp.name));
      const b = stem(ln);
      return a === b || (a.length >= 3 && b.startsWith(a)) || (b.length >= 3 && a.startsWith(b));
    });
    if (p) set(p, nums);
  }
}
